import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PDFExport } from "@progress/kendo-react-pdf";
import { setExportTask } from "../../../actions/taskSidebar";
import { formatDate } from "../../../helper/common";
import { getTaskUrl } from "../../../helper/common";
import { getFormSubmission } from "../../../shared/services/workflow.services";
import { getAttachments } from "../../Tasks/tasks.service";
import { quote, route } from "../../../config";
import config from "../../../env.config";
import { sanitizeUrl } from "../../../utils/common";
import "../../../editor/editor.scss";
import "./TaskPDFExport.scss";
import { renderForms, renderReviewSection } from "./exportRenderForms";
import {
  renderDescription,
  renderApprovals,
  renderEmails,
  renderAttachments,
  renderActivityLogs,
  renderComments,
  renderLinkedTasks,
  renderTags,
  renderFollowers,
  renderSection
} from "./exportSectionRenderers";
import {
  attemptPDFExport,
  createTaskData,
  shouldRenderSection
} from "./exportUtils";

/**
 * TaskPDFExport component for generating PDF exports of task details
 * Refactored into modular components for better maintainability
 * @author Bhavana
 */
const TaskPDFExport = () => {
  const dispatch = useDispatch();
  const pdfExportRef = useRef(null);
  const exportAttemptedRef = useRef(false);
  const { exportTask, task, stages, approvalLogs } = useSelector((state) => state.taskSidebar);
  const { assigneeList } = useSelector((state) => state.tasks?.defaultDetails);
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { operationalTeamName } = useSelector((state) => state.auth.user);
  const user = useSelector((state) => state.auth.user);
  const { tenantAppUrl } = useSelector((state) => state.app);
  const [formData, setFormData] = useState(null);
  const [formDataChecked, setFormDataChecked] = useState(false);
  const [taskAttachments, setTaskAttachments] = useState([]);

  const priority = useMemo(() =>
    defaultDetails?.priorityList?.find((c) => c.value === task?.Priority),
    [task?.Priority, defaultDetails?.priorityList]
  );

  const stage = useMemo(() =>
    stages?.find((c) => c?.value == task?.StageId),
    [task?.StageId, stages]
  );

  // Create task data object
  const taskData = useMemo(() =>
    createTaskData(task, priority, stage, formatDate, assigneeList),
    [task, priority, stage]
  );

  const fullTaskUrl = useMemo(() => {
    if (!task?.taskId) {
      return "";
    }
    const baseUrl = tenantAppUrl || config.BASE_URL.APP_URL;
    return `${baseUrl}${sanitizeUrl(getTaskUrl(task.taskId))}`;
  }, [tenantAppUrl, task?.taskId]);

  // Use preloaded form data or fetch if not available
  useEffect(() => {
    const fetchFormData = async () => {
      const exportNeedsFormSubmission = exportTask?.sections?.includes("forms") || exportTask?.sections?.includes("reviewSection");
      const shouldFetchForms = exportTask?.isExporting && exportNeedsFormSubmission;
      if (!shouldFetchForms) {
        setFormDataChecked(true);
        return;
      }
      setFormDataChecked(false);
      if (exportTask?.preloadedFormData) {
        setFormData(exportTask.preloadedFormData);
        setFormDataChecked(true);
        return;
      }
      if (task?.submittedFormId) {
        try {
          const submittedFormData = await getFormSubmission({
            submittedFormId: task?.submittedFormId,
          });
          setFormData(submittedFormData || null);
        } catch (error) {
          console.error(quote.ERROR_FETCHING_FORM_DATA_FOR_EXPORT, error);
          setFormData(null);
        }
      } else {
        setFormData(null);
      }
      setFormDataChecked(true);
    };

    fetchFormData();
  }, [exportTask?.isExporting, task?.submittedFormId, exportTask?.sections, exportTask?.preloadedFormData]);

  // Fetch task attachments for file upload elements
  useEffect(() => {
    const fetchTaskAttachments = async () => {
      const attachmentsForSections = exportTask?.sections?.includes("forms") || exportTask?.sections?.includes("reviewSection");
      if (exportTask?.isExporting && task?.taskId && attachmentsForSections) {
        try {
          const attachments = await getAttachments(route.PRIVATE_ROUTE.TASKS.ENTITY_NAME, task?.taskId);
          setTaskAttachments(attachments || []);
        } catch (error) {
          console.error("Error fetching task attachments:", error);
          setTaskAttachments([]);
        }
      }
    };

    fetchTaskAttachments();
  }, [exportTask?.isExporting, task?.taskId, exportTask?.sections]);

  useEffect(() => {
    if (exportTask?.isExporting) {
      exportAttemptedRef.current = false;
      setFormDataChecked(false);
    }
  }, [exportTask?.isExporting]);

  useEffect(() => {
    if (exportTask?.isExporting && !exportAttemptedRef.current) {
      const shouldWaitForFormData = exportTask?.sections?.includes("forms") || exportTask?.sections?.includes("reviewSection");
      const isFormDataReady = !shouldWaitForFormData || formDataChecked;

      if (isFormDataReady) {
        exportAttemptedRef.current = true;
        setTimeout(() => {
          attemptPDFExport(pdfExportRef, dispatch, setExportTask, setFormData);
        }, 1500);
      }
    }
  }, [exportTask?.isExporting, formDataChecked, exportTask?.sections]);

  const renderConditionalSection = (sectionKey, sectionTitle, renderFunction) => {
    if (!shouldRenderSection(exportTask, sectionKey)) {
      return null;
    }

    const content = renderFunction();
    return renderSection(sectionKey, sectionTitle, content, formData, assigneeList);
  };

  if (!exportTask?.isExporting) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: '-100vh',
      left: '0',
      width: '210mm',
      height: 'auto',
      zIndex: -1000,
      pointerEvents: 'none',
      opacity: 0
    }}>
      <PDFExport
        ref={pdfExportRef}
        fileName={exportTask?.fileName || "task-export"}
        paperSize="A4"
        margin="1cm"
      >
        <div className="pdf-export-container">
          {/* Header */}
          <div className="pdf-header text-center mb-4">
            <div className="ticket-summary">
              {fullTaskUrl ? (
                <a href={fullTaskUrl} target="_blank" rel="noopener noreferrer">
                  <h2 className="ticket-id-title mb-3">{taskData.ticketId}: {taskData.title}</h2>
                </a>
              ) : (
                <h2 className="ticket-id-title mb-3">{taskData.ticketId}: {taskData.title}</h2>
              )}
            </div>
          </div>

          <div className="ticket-details-table mb-4">
            <table className="details-table w-100 mb-3">
              <thead>
                <tr>
                  <th className="p-2 text-left">Created By</th>
                  <th className="p-2 text-left">Created</th>
                  <th className="p-2 text-left">Due Date</th>
                  <th className="p-2 text-left">Priority</th>
                  {taskData.isCompleted && <th className="p-2 text-left">Status</th>}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 text-left">{taskData.createdBy}</td>
                  <td className="p-2 text-left">{taskData.created}</td>
                  <td className="p-2 text-left">{taskData.dueDate}</td>
                  <td className="p-2 text-left">{taskData.priority}</td>
                  {taskData.isCompleted && <td className="p-2 text-left">Closed</td>}
                </tr>
              </tbody>
            </table>
          </div>

          {!taskData.isCompleted && (
            <div className="current-status-section pdf-section mb-4">
              <h6 className="current-status-title section-title mb-2 pb-1">Current Status</h6>
              <div className="section-content ml-2">
                <table className="current-status-table">
                  <thead>
                    <tr>
                      <th>{operationalTeamName}</th>
                      <th>Assignee</th>
                      <th>Stage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{taskData.projectName || "N/A"}</td>
                      <td>{taskData.assigneeName}</td>
                      <td>{taskData.currentStage}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="pdf-sections">
            {renderConditionalSection('approvals', 'Approvals', () => renderApprovals(approvalLogs))}
            {renderConditionalSection('description', 'Description', () => renderDescription(taskData))}
            {renderConditionalSection('forms', 'Form', () => renderForms(formData, taskAttachments, user, task))}
            {renderConditionalSection('reviewSection', 'Review Section', () =>renderReviewSection(formData, taskAttachments, user, task, assigneeList))}
            {renderConditionalSection('emails', 'Emails', renderEmails)}
            {renderConditionalSection('attachments', 'Attachments', renderAttachments)}
            {renderConditionalSection('activityLogs', 'Activity Logs', renderActivityLogs)}
            {renderConditionalSection('comments', 'Comments', renderComments)}
            {renderConditionalSection('linkedTasks', 'Linked Tasks', renderLinkedTasks)}
            {renderConditionalSection('tags', 'Tags', renderTags)}
            {renderConditionalSection('followers', 'Followers', renderFollowers)}
          </div>
        </div>
      </PDFExport>
    </div>
  );
};

export default TaskPDFExport;
